import React, { useState, useEffect, useRef } from 'react';
import usePushToTalk from '../hooks/usePushToTalk';
import Recorder from './Recorder';
import { useRecording } from '../contexts/RecordingContext';
import { escapeRegExp } from '../utils/regex';
import './RecordingModal.css';

export default function RecordingModal({ 
  isOpen, 
  onClose, 
  context, 
  onTranscriptReceived
}) {
  const [transcript, setTranscript] = useState('');
  const [localTransactions, setLocalTransactions] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processingTransaction, setProcessingTransaction] = useState(false);
  const [error, setError] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [currentRequestId, setCurrentRequestId] = useState(null);
  
  const { start, stop, recording, starting, supported } = usePushToTalk();
  const { sendAudio, commitTransactions, cancelTransactions, setCurrentContext, progressUpdates } = useRecording();
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const cancelRecordingRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when closing
      setTranscript('');
      setLocalTransactions([]);
      setProcessing(false);
      setError(null);
      setRecordingTime(0);
      setProgressText('');
      setCurrentRequestId(null);
      setCurrentContext(null);
      return;
    }

    // Set context when modal opens
    setCurrentContext(context);
    
    // Cancel any pending transactions from previous session when opening modal
    cancelTransactions().catch(() => {
      // Ignore errors - there might not be any transactions
    });
    
    // Start recording when modal opens
    if (supported && !recording && !starting) {
      start();
      recordingStartTimeRef.current = Date.now();
      startTimer();
    }
  }, [isOpen, supported, context, setCurrentContext]);
  
  // Handle progress updates from shared socket
  useEffect(() => {
    if (!currentRequestId || !progressUpdates[currentRequestId]) return;
    
    const { phase, data } = progressUpdates[currentRequestId];
    
    switch (phase) {
      case 'recognition_started':
        setProgressText('Transcribing audio…');
        break;
      case 'recognition_done':
        setProgressText(`You said: "${data.text}"\nPlanning actions…`);
        break;
      case 'command_detect_started':
      case 'classify_started':
        setProgressText('Planning actions…');
        break;
      case 'classify_done':
        setProgressText('Thinking…');
        break;
      case 'llm_start':
        setProgressText('Thinking…');
        break;
      case 'tool_start': {
        const labels = (data.tools || []).map(t => t.label || t.name).join(', ');
        setProgressText(labels);
        break;
      }
      case 'tool':
        if (data.parallel && data.names && data.names.length > 1) {
          setProgressText(`Running ${data.names.length} tools: ${data.names.join(', ')}…`);
        } else {
          setProgressText(`${data.name}…`);
        }
        break;
      case 'final':
        setTranscript(data.text || 'No response');
        setProgressText('');
        setProcessing(false);
        
        // Handle transactions
        if (data.transactions && data.transactions.length > 0) {
          setLocalTransactions(data.transactions);
        }
        
        // Notify parent if callback provided
        if (onTranscriptReceived) {
          onTranscriptReceived(data);
        }
        break;
      case 'error':
        setError(data.message || 'An error occurred');
        setProgressText('');
        setProcessing(false);
        break;
      default:
        setProgressText(phase);
    }
  }, [progressUpdates, currentRequestId, onTranscriptReceived]);

  useEffect(() => {
    if (recording) {
      recordingStartTimeRef.current = Date.now();
      startTimer();
    } else {
      stopTimer();
      setRecordingTime(0);
    }
  }, [recording]);

  const startTimer = () => {
    stopTimer();
    recordingTimerRef.current = setInterval(() => {
      if (recordingStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        setRecordingTime(elapsed);
      }
    }, 100);
  };

  const stopTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleAudioReady = async (audioBlob) => {
    if (!audioBlob) {
      setError('Recording too short or failed');
      return;
    }

    setProcessing(true);
    setError(null);
    setProgressText('Uploading audio…');
    
    // Set request ID from the timestamp that sendAudio will use
    const requestId = Date.now();
    setCurrentRequestId(requestId);

    try {
      const result = await sendAudio(audioBlob, context);
      
      if (!result.success) {
        setError(result.error || 'Failed to process recording');
        setProcessing(false);
        setProgressText('');
      }
      // Success case will be handled by socket progress updates
    } catch (err) {
      console.error('Transcription error:', err);
      setError('Failed to process recording');
      setProcessing(false);
      setProgressText('');
    }
  };

  const handleTransactionCommit = async () => {
    if (processingTransaction) return;
    
    // Clear transactions immediately to hide buttons and prevent double-clicks
    setLocalTransactions([]);
    setProcessingTransaction(true);
    
    try {
      const result = await commitTransactions();
      
      if (result.success) {
        // Replace fake IDs with real IDs in the transcript
        if (result.idMap && Object.keys(result.idMap).length > 0) {
          setTranscript(currentText => {
            if (!currentText) return currentText;
            
            let updatedText = currentText;
            for (const [fakeId, realId] of Object.entries(result.idMap)) {
              // Replace all occurrences of the fake ID with the real ID
              updatedText = updatedText.replace(
                new RegExp(`\\b${escapeRegExp(fakeId)}\\b`, 'g'),
                realId
              );
            }
            
            // Add a note about the ID changes
            const idChanges = Object.entries(result.idMap)
              .map(([fake, real]) => `${fake} → ${real}`)
              .join(', ');
            
            return updatedText + `\n\n✅ Changes applied! IDs updated: ${idChanges}`;
          });
        }
        
        // Notify parent to refresh with idMap
        if (onTranscriptReceived) {
          onTranscriptReceived({ 
            answer: 'Changes applied successfully', 
            transactionsCommitted: true,
            idMap: result.idMap
          });
        }
        
        // Wait a moment for user to see the ID updates, then close
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Keep modal open and show error
        setError('Failed to apply changes: ' + (result.error || 'Unknown error'));
        setProcessingTransaction(false);
      }
    } catch (err) {
      console.error('Transaction commit error:', err);
      // Keep modal open and show error
      setError('Error applying changes');
      setProcessingTransaction(false);
    }
  };

  const handleTransactionCancel = async () => {
    if (processingTransaction) return;
    
    // Clear transactions immediately to hide buttons and prevent double-clicks
    setLocalTransactions([]);
    setProcessingTransaction(true);
    
    try {
      const result = await cancelTransactions();
      
      if (result.success) {
        // Close modal immediately on success
        onClose();
      } else {
        // Keep modal open and show error
        setError('Failed to cancel transactions: ' + (result.error || 'Unknown error'));
        setProcessingTransaction(false);
      }
    } catch (err) {
      console.error('Transaction cancel error:', err);
      // Keep modal open and show error
      setError('Error cancelling transactions');
      setProcessingTransaction(false);
    }
  };

  const handleClose = async () => {
    // Always stop recording if in progress
    if (recording) {
      stop(); // This will discard the recording
    }
    
    // Cancel any pending transactions without confirmation
    if (localTransactions.length > 0) {
      await cancelTransactions();
    }
    
    // Clear all state
    setLocalTransactions([]);
    setTranscript('');
    setProgressText('');
    setError(null);
    stopTimer();
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="recording-modal-overlay">
      <div className="recording-modal">
        <div className="recording-modal-header">
          <button className="recording-modal-close" onClick={handleClose}>
            ✕
          </button>
          {context?.issueKey && (
            <div className="recording-issue-badge">{context.issueKey}</div>
          )}
          {/* Show follow-up mic button when response is ready */}
          {transcript && !processing && !recording && (
            <button 
              className="recording-modal-follow-up"
              onClick={async () => {
                // Cancel any pending transactions first
                if (localTransactions.length > 0) {
                  await cancelTransactions();
                }
                
                // Clear all state for new recording
                setLocalTransactions([]);
                setTranscript('');
                setProgressText('');
                setError(null);
                setCurrentRequestId(null);
                
                // Start new recording
                if (supported && !recording && !starting) {
                  start();
                  recordingStartTimeRef.current = Date.now();
                  startTimer();
                }
              }}
              title="Record follow-up"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.7806 10.1963C16.1326 10.3011 16.3336 10.6714 16.2288 11.0234L16.1487 11.2725C15.3429 13.6262 13.2236 15.3697 10.6644 15.6299L10.6653 16.835H12.0833L12.2171 16.8486C12.5202 16.9106 12.7484 17.1786 12.7484 17.5C12.7484 17.8214 12.5202 18.0894 12.2171 18.1514L12.0833 18.165H7.91632C7.5492 18.1649 7.25128 17.8672 7.25128 17.5C7.25128 17.1328 7.5492 16.8351 7.91632 16.835H9.33527L9.33429 15.6299C6.775 15.3697 4.6558 13.6262 3.84992 11.2725L3.76984 11.0234L3.74445 10.8906C3.71751 10.5825 3.91011 10.2879 4.21808 10.1963C4.52615 10.1047 4.84769 10.2466 4.99347 10.5195L5.04523 10.6436L5.10871 10.8418C5.8047 12.8745 7.73211 14.335 9.99933 14.335C12.3396 14.3349 14.3179 12.7789 14.9534 10.6436L15.0052 10.5195C15.151 10.2466 15.4725 10.1046 15.7806 10.1963ZM12.2513 5.41699C12.2513 4.17354 11.2437 3.16521 10.0003 3.16504C8.75675 3.16504 7.74835 4.17343 7.74835 5.41699V9.16699C7.74853 10.4104 8.75685 11.418 10.0003 11.418C11.2436 11.4178 12.2511 10.4103 12.2513 9.16699V5.41699ZM13.5814 9.16699C13.5812 11.1448 11.9781 12.7479 10.0003 12.748C8.02232 12.748 6.41845 11.1449 6.41828 9.16699V5.41699C6.41828 3.43889 8.02221 1.83496 10.0003 1.83496C11.9783 1.83514 13.5814 3.439 13.5814 5.41699V9.16699Z"></path>
              </svg>
            </button>
          )}
          {!transcript && <div style={{ width: '32px' }}></div>}
        </div>

        <div className="recording-modal-content">
          <h3>{context?.summary || 'Recording'}</h3>

          <div className="recording-status">
            {starting ? (
              <div className="recording-starting">Starting microphone...</div>
            ) : recording ? (
              <>
                <div className="recording-indicator">
                  <span className="recording-dot"></span>
                  Recording...
                </div>
                <div className="recording-time">{formatTime(recordingTime)}</div>
              </>
            ) : (
              <div className="recording-idle">Ready</div>
            )}
          </div>

          {/* Recording controls - only show when actively recording */}
          {(recording || starting) && (
            <div className="recording-controls">
              <Recorder 
                onBlobReady={handleAudioReady}
                start={start}
                stop={stop}
                recording={recording}
                starting={starting}
                supported={supported}
                onStopClick={stopRecordingRef}
                onCancelClick={cancelRecordingRef}
              />
            </div>
          )}

          {error && (
            <div className="recording-error">
              {error}
            </div>
          )}

          {/* Show transcript when available, or progress text when processing */}
          {transcript && !recording && (
            <div className="recording-transcript">
              <h4>Response:</h4>
              <div className="transcript-content">
                {transcript.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            </div>
          )}
          
          {/* Show progress updates at the bottom */}
          {progressText && !transcript && (
            <div className="recording-transcript">
              <h4>Progress:</h4>
              <div className="transcript-content">
                {progressText.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            </div>
          )}

          {localTransactions.length > 0 && (
            <div className="recording-transactions">
              <h4>Pending Transactions:</h4>
              <div className="transaction-list">
                {localTransactions.map((transaction, index) => (
                  <div key={index} className="transaction-item">
                    <span className="transaction-type">{transaction.name || transaction}</span>
                    {transaction.status === 'completed' && (
                      <span className="transaction-status">✓</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="transaction-controls">
                <button 
                  className="transaction-commit-btn"
                  onClick={handleTransactionCommit}
                  disabled={processingTransaction}
                >
                  {processingTransaction ? 'Processing...' : 'Commit Changes'}
                </button>
                <button 
                  className="transaction-cancel-btn"
                  onClick={handleTransactionCancel}
                  disabled={processingTransaction}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}